import { api } from '../api/client';

import {
	API_CARD_LAYOUTS_URL,
	API_CARD_TABLE_LAYOUT_URL,
	API_CARD_TILE_LAYOUT_URL,
	API_COLLECTION_CARD_LAYOUTS_URL,
} from '../utils';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * Card layouts: how a card's items are ARRANGED, cascading through three
 * tiers — card, then collection, then user, the last one written winning.
 * A card with no row in any tier is simply absent, and the client falls back
 * to picking fields automatically (itemValueDisplay.pickDisplayFields), which
 * is what every card does until somebody arranges one.
 *
 * Wire shape (keepr-api docs/SCHEMA.md § Card layouts):
 *   { _id, card_id, kind: 'tile', scope: 'card'|'collection'|'user',
 *     collection_id, userId, body: { cols: 6, rows: [[{ element, span }]] }, … }
 *
 * A tile layout is a SETTING, not a named record — one per tier — so it is
 * saved with a PUT and reset with a DELETE. Denials follow the platform line:
 * somebody else's user tier is a 404, and a tier the caller may not write is
 * a 403 with a sentence the UI can print.
 */

/**
 * Every tier of one card the caller may see, plus `resolved` — the one that
 * applies. Pass `collectionId` to include that collection's tier and the
 * caller's own; without it only the card's own answer comes back.
 */
export async function getCardLayouts(cardId, { collectionId = null, kind = 'tile' } = {}) {
	const params = { kind };
	if (collectionId) params.collection_id = collectionId;
	const { data } = await api.get(API_CARD_LAYOUTS_URL(cardId), { params });
	return {
		layouts: Array.isArray(data?.layouts) ? data.layouts : [],
		resolved: data?.resolved || null,
	};
}

/**
 * Saves one tier. Upsert — saving twice leaves one row, so two tabs cannot
 * produce a duplicate. `collectionId` is required for the collection and user
 * tiers and meaningless for the card tier.
 */
export async function putTileLayout(cardId, { scope, collectionId = null, body }) {
	const payload = { scope, body };
	if (collectionId) payload.collection_id = collectionId;
	const { data } = await api.put(API_CARD_TILE_LAYOUT_URL(cardId), payload);
	return data;
}

/** "Reset to inherited": drops one tier so the cascade falls through. */
export async function deleteTileLayout(cardId, { scope, collectionId = null }) {
	const params = { scope };
	if (collectionId) params.collection_id = collectionId;
	const { data } = await api.delete(API_CARD_TILE_LAYOUT_URL(cardId), { params });
	return data;
}

/**
 * A TABLE layout — which columns the item table shows for one card — is a
 * setting exactly like a tile layout: one per tier, PUT to save, DELETE to
 * fall back. Body `{ columns: [{ element | system, width?, frozen? }], sort? }`
 * (keepr-api docs/SCHEMA.md § Card layouts).
 */
export async function putTableLayout(cardId, { scope, collectionId = null, body }) {
	const payload = { scope, body };
	if (collectionId) payload.collection_id = collectionId;
	const { data } = await api.put(API_CARD_TABLE_LAYOUT_URL(cardId), payload);
	return data;
}

export async function deleteTableLayout(cardId, { scope, collectionId = null }) {
	const params = { scope };
	if (collectionId) params.collection_id = collectionId;
	const { data } = await api.delete(API_CARD_TABLE_LAYOUT_URL(cardId), { params });
	return data;
}

/**
 * Page layouts of one card the caller can see — every tier, not a cascade: a
 * page is a NAMED record and a card carries many, so what a reader wants is
 * the whole gallery, not a winner.
 */
export async function getPageLayouts(cardId, { collectionId = null } = {}) {
	const { layouts } = await getCardLayouts(cardId, { collectionId, kind: 'page' });
	return layouts;
}

/**
 * A card's FORM layout (kind 'form'): the order and groups of the item form
 * and read view. Card tier only, inherited down the card hierarchy — the API
 * resolves the nearest card in the chain, so `resolved.card_id` is the card
 * that OWNS the answer (this card, or an ancestor), and `own` is this card's
 * own row or null. Both null: automatic grouping.
 */
export async function getFormLayout(cardId) {
	const { layouts, resolved } = await getCardLayouts(cardId, { kind: 'form' });
	return { own: layouts[0] || null, resolved };
}

/** Saves the card's form layout (upsert, card tier). Body `{ groups }`. */
export async function putFormLayout(cardId, body) {
	const { data } = await api.put(`${API_CARD_LAYOUTS_URL(cardId)}/form`, { scope: 'card', body });
	return data;
}

/** Drops the card's own form layout: it falls back to its parent's, or automatic. */
export async function deleteFormLayout(cardId) {
	const { data } = await api.delete(`${API_CARD_LAYOUTS_URL(cardId)}/form`);
	return data;
}

/** Creates a named page layout in one tier. 409 on a duplicate name. */
export async function createPageLayout(cardId, { scope, collectionId = null, name, body, elementChoices = null }) {
	const payload = { kind: 'page', scope, name, body };
	if (collectionId) payload.collection_id = collectionId;
	// The person's answer to an ambiguous element name in a query block (utils/elementRefusal.js).
	const { data } = await api.post(API_CARD_LAYOUTS_URL(cardId), withElementChoicesField(payload, elementChoices));
	return data;
}

/** Renames or re-lays-out one page layout. `scope` is not patchable. */
export async function updatePageLayout(cardId, layoutId, patch) {
	const { data } = await api.patch(`${API_CARD_LAYOUTS_URL(cardId)}/${layoutId}`, patch);
	return data;
}

export async function deletePageLayout(cardId, layoutId) {
	const { data } = await api.delete(`${API_CARD_LAYOUTS_URL(cardId)}/${layoutId}`);
	return data;
}

/**
 * A page layout's FORM (KPR-1859, keepr-api docs/SCHEMA.md § A form on a page
 * layout): a provider's own paper form, stored on the layout and drawn by the
 * server page by page. `body.form` is the server's, written only by these
 * routes, and an answer's `{ layout, changes }` is the row as it now stands.
 *
 * The layout must exist first. The upload answers when the pages are drawn, so
 * nothing else may save the layout meanwhile, and a 409 `stale_layout` is never
 * retried: the person has to look at what changed.
 */
const layoutFormUrl = (cardId, layoutId) => `${API_CARD_LAYOUTS_URL(cardId)}/${layoutId}/form`;

/** Attach, or replace the form there. `onProgress(fraction)` follows the bytes; the drawing that follows has none. */
export async function attachLayoutForm(cardId, layoutId, file, { onProgress } = {}) {
	const form = new FormData();
	form.append('file', file);
	const { data } = await api.post(layoutFormUrl(cardId, layoutId), form, {
		onUploadProgress: (event) => {
			const total = event?.total || file?.size || 0;
			if (total > 0 && typeof onProgress === 'function') onProgress(Math.min(1, (event.loaded || 0) / total));
		},
	});
	return data;
}

/** What the form is, with its malware word (`scan`): what the layout's body does not say. */
export async function getLayoutForm(cardId, layoutId, { collectionId = null } = {}) {
	const { data } = await api.get(layoutFormUrl(cardId, layoutId), { params: collectionId ? { collection_id: collectionId } : {} });
	return data;
}

/** An image form's paper size. A PDF says its own, and refuses this. */
export async function setLayoutFormPaper(cardId, layoutId, paper) {
	const { data } = await api.patch(layoutFormUrl(cardId, layoutId), { paper });
	return data;
}

/** Takes the form off. Every block stays where it is. */
export async function removeLayoutForm(cardId, layoutId) {
	const { data } = await api.delete(layoutFormUrl(cardId, layoutId));
	return data;
}

/**
 * One page of the form, as a Blob, through the authenticated client: the route
 * takes a bearer token, so an <img src> cannot read it. `file` is the form's id,
 * so a replaced form is a new URL. `collectionId` is where the reader is printing
 * from, which is how a sub-collection's reader reaches a parent's layout. A
 * refusal's body arrives as a Blob too, so it is read back into words here and
 * a caller can see its `code`.
 */
export async function fetchLayoutFormPage(cardId, layoutId, page, { file, collectionId = null, signal } = {}) {
	const params = { file, ...(collectionId ? { collection_id: collectionId } : {}) };
	try {
		const { data } = await api.get(`${layoutFormUrl(cardId, layoutId)}/pages/${page}`, { params, responseType: 'blob', signal });
		return data;
	} catch (error) {
		const body = error?.response?.data;
		if (body && typeof body.text === 'function') {
			try {
				error.response.data = JSON.parse(await body.text());
			} catch {
				error.response.data = null;
			}
		}
		throw error;
	}
}

/**
 * Moves a page layout one tier: promote walks user -> collection -> card,
 * demote walks back. Demoting off the card tier names the collection it lands
 * in, since the card tier belongs to none.
 */
export async function movePageLayoutTier(cardId, layoutId, direction, { collectionId = null } = {}) {
	const body = collectionId ? { collection_id: collectionId } : {};
	const { data } = await api.post(`${API_CARD_LAYOUTS_URL(cardId)}/${layoutId}/${direction}`, body);
	return data;
}

/**
 * The item grid's one request: cardId -> the layout that applies to this
 * reader, already cascaded. Cards nobody has arranged are absent.
 */
export async function getCollectionTileLayouts(collectionId) {
	return getCollectionCardLayouts(collectionId, 'tile');
}

/** The same one request for any setting kind — `table` for the item table. */
export async function getCollectionCardLayouts(collectionId, kind = 'tile') {
	const { data } = await api.get(API_COLLECTION_CARD_LAYOUTS_URL(collectionId), { params: { kind } });
	return data?.layouts && typeof data.layouts === 'object' ? data.layouts : {};
}
